import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  BespokeTemplateImportUpsertRow,
  BespokeTemplateStatus,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { BespokeTemplateExportRow } from './bespokeTemplatesExport';
import { compareFormGroupKeys } from './formPrecedence';

/** Schema version strings that do not appear in any loaded catalog slice group here. */
export const IMPORT_UNKNOWN_FORM_TYPE = '__import_unknown__' as const;

const compareUnknownLast = compareFormGroupKeys(IMPORT_UNKNOWN_FORM_TYPE);

export type ImportFormTypeGroupingKey =
  RogerDocFormType | typeof IMPORT_UNKNOWN_FORM_TYPE;

/** Stable leaf id for bundle rows (natural upsert key as string). */
export function bespokeTemplateLeafKey(row: {
  form_type: string;
  schema_version_name: string;
  form_schema_version: string;
  section_name: string;
  name: string;
}): string {
  return [
    row.form_type.trim(),
    row.schema_version_name.trim(),
    row.form_schema_version.trim(),
    row.section_name.trim(),
    row.name.trim(),
  ].join('\0');
}

export function exportRowsToImportUpserts(
  rows: BespokeTemplateExportRow[]
): BespokeTemplateImportUpsertRow[] {
  return rows.map(row => ({
    form_type: row.form_type,
    schema_version_name: row.schema_version_name.trim(),
    form_schema_version: row.form_schema_version.trim(),
    section_name: row.section_name.trim(),
    name: row.name.trim(),
    layout_json: row.layout_json ?? undefined,
    description: row.description ?? null,
    status: row.status,
  }));
}

export type BespokeParsedImportEnvelope = {
  formatVersion: number;
  /** `true` for `format_version` <= 4 — rows need {@link resolveLegacyImportRowPins}. */
  needsPinInference: boolean;
  templates: BespokeTemplateExportRow[];
  exportScope?: string;
  exportedAt?: string;
  selectedFormTypes?: RogerDocFormType[];
};

function requireString(rec: Record<string, unknown>, k: string): string {
  const v = rec[k];
  if (typeof v !== 'string' || !v.trim()) {
    throw new Error(`Invalid template row: missing or empty "${k}"`);
  }
  return v.trim();
}

function parseRowStatus(rec: Record<string, unknown>): BespokeTemplateStatus {
  const raw = rec.status;
  if (raw === 'live' || raw === 'paused' || raw === 'standby') {
    return raw;
  }
  return 'standby';
}

/**
 * `format_version` <= 4 bundles predate the RogerDoc pin — rows only carry
 * `schema_version_name`. Rows are coerced with this placeholder and resolved
 * against the current catalog by {@link resolveLegacyImportRowPins} once it's
 * available (ambiguous names can't be resolved and are reported separately).
 */
const UNRESOLVED_LEGACY_FORM_TYPE = '' as RogerDocFormType;

function coerceTemplateExportRow(
  item: unknown,
  formatVersion: number
): BespokeTemplateExportRow {
  if (!item || typeof item !== 'object') {
    throw new Error('Invalid templates[] entry (expected object)');
  }
  const r = item as Record<string, unknown>;
  const hasPinFields = formatVersion >= 5;
  return {
    form_type: hasPinFields
      ? (requireString(r, 'form_type') as RogerDocFormType)
      : UNRESOLVED_LEGACY_FORM_TYPE,
    schema_version_name: requireString(r, 'schema_version_name'),
    form_schema_version: hasPinFields
      ? requireString(r, 'form_schema_version')
      : '',
    section_name: requireString(r, 'section_name'),
    name: requireString(r, 'name'),
    layout_json: (() => {
      if (!('layout_json' in r)) return undefined;
      if (r.layout_json == null) return null;
      if (!Array.isArray(r.layout_json)) {
        throw new Error(
          `Invalid layout_json for "${requireString(r, 'name')}": expected array or null, got ${typeof r.layout_json}`
        );
      }
      return r.layout_json as unknown[];
    })(),
    description:
      r.description === null || typeof r.description === 'string'
        ? (r.description as string | null)
        : undefined,
    status: parseRowStatus(r),
  };
}

/** Sort/dedup key tolerant of legacy rows that don't have a resolved pin yet. */
function importRowDedupeKey(row: BespokeTemplateExportRow): string {
  return [
    row.form_type,
    row.schema_version_name.trim(),
    row.form_schema_version.trim(),
    row.section_name.trim(),
    row.name.trim(),
  ].join('\0');
}

/**
 * Parses bundled exports: ``format_version`` 1–5. v5 rows carry the full pin
 * (`form_type` + `form_schema_version`); v≤4 rows need {@link resolveLegacyImportRowPins}
 * before they can be submitted.
 */
export function parseBespokeImportBundleJson(
  raw: unknown
): BespokeParsedImportEnvelope {
  if (!raw || typeof raw !== 'object') {
    throw new Error('JSON root must be an object');
  }
  const root = raw as Record<string, unknown>;
  const fv = root.format_version;
  if (fv !== 1 && fv !== 2 && fv !== 3 && fv !== 4 && fv !== 5) {
    throw new Error(
      `Unsupported format_version (${String(fv)}). Expected 1–5.`
    );
  }

  const templatesRaw = root.templates;
  if (!Array.isArray(templatesRaw)) {
    throw new Error('Missing "templates" array');
  }

  const byKey = new Map<string, BespokeTemplateExportRow>();
  let index = 0;
  for (const item of templatesRaw) {
    index += 1;
    try {
      const row = coerceTemplateExportRow(item, fv);
      byKey.set(importRowDedupeKey(row), row);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      throw new Error(`templates[${index - 1}]: ${msg}`);
    }
  }

  const selectedFormTypes =
    Array.isArray(root.selected_form_types) &&
    root.selected_form_types.every(t => typeof t === 'string')
      ? [...(root.selected_form_types as RogerDocFormType[])]
      : undefined;

  return {
    formatVersion: fv,
    needsPinInference: fv < 5,
    templates: [...byKey.values()].sort((a, b) =>
      importRowDedupeKey(a).localeCompare(importRowDedupeKey(b), undefined, {
        sensitivity: 'base',
      })
    ),
    exportScope:
      typeof root.export_scope === 'string' ? root.export_scope : undefined,
    exportedAt:
      typeof root.exported_at === 'string' ? root.exported_at : undefined,
    selectedFormTypes,
  };
}

/** Rows resolved against the current catalog, split from those needing manual fixup. */
export type LegacyPinResolution = {
  rows: BespokeTemplateExportRow[];
  unresolved: BespokeTemplateExportRow[];
};

/**
 * Legacy bundles (`format_version` <= 4) don't carry `form_type` /
 * `form_schema_version` per row. Infers them from the current catalog when
 * `schema_version_name` maps to exactly one `{form_type, form_schema_version}`
 * pin; rows whose name is unknown, shared across form types, or shared across
 * multiple versions within one form type are returned as `unresolved`.
 */
export function resolveLegacyImportRowPins(params: {
  rows: BespokeTemplateExportRow[];
  catalog: RogerDocFormTypeCatalogEntry[];
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
}): LegacyPinResolution {
  // name -> formType -> distinct form_schema_version values in the catalog.
  const versionsByName = new Map<string, Map<RogerDocFormType, Set<string>>>();
  for (const entry of params.catalog) {
    const ft = entry.form_type as RogerDocFormType;
    for (const v of params.versionsByFormType.get(ft) ?? []) {
      const nm = (v.name ?? '').trim();
      const ver = (v.version ?? '').trim();
      if (!nm || !ver) continue;
      let byFormType = versionsByName.get(nm);
      if (!byFormType) {
        byFormType = new Map();
        versionsByName.set(nm, byFormType);
      }
      let versions = byFormType.get(ft);
      if (!versions) {
        versions = new Set();
        byFormType.set(ft, versions);
      }
      versions.add(ver);
    }
  }

  const rows: BespokeTemplateExportRow[] = [];
  const unresolved: BespokeTemplateExportRow[] = [];
  for (const row of params.rows) {
    if (row.form_type) {
      rows.push(row);
      continue;
    }
    const trimmedName = row.schema_version_name.trim();
    const byFormType = versionsByName.get(trimmedName);
    if (!byFormType || byFormType.size !== 1) {
      unresolved.push(row);
      continue;
    }
    const [[ft, versions]] = byFormType;
    if (versions.size !== 1) {
      unresolved.push(row);
      continue;
    }
    const [formSchemaVersion] = versions;
    rows.push({
      ...row,
      form_type: ft,
      form_schema_version: formSchemaVersion,
    });
  }
  return { rows, unresolved };
}

export type ImportSectionGroup = {
  section_name: string;
  leaves: BespokeTemplateExportRow[];
};

export type ImportSchemaGroup = {
  schema_version_name: string;
  sections: ImportSectionGroup[];
};

export type ImportFormTypeGroup = {
  formTypeKey: ImportFormTypeGroupingKey;
  formTypeDisplayLabel: string;
  schemas: ImportSchemaGroup[];
};

function sortSections(sections: ImportSectionGroup[]): void {
  sections.sort((a, b) =>
    a.section_name.localeCompare(b.section_name, undefined, {
      sensitivity: 'base',
    })
  );
  for (const s of sections) {
    s.leaves.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }
}

/** Groups rows for nested checkbox UI: form type → schema version → section → templates. */
export function buildImportNestedGroups(params: {
  templates: BespokeTemplateExportRow[];
  catalog: RogerDocFormTypeCatalogEntry[];
}): ImportFormTypeGroup[] {
  const displayByFormType = new Map<
    RogerDocFormType,
    RogerDocFormTypeCatalogEntry['display_label']
  >();
  for (const row of params.catalog) {
    displayByFormType.set(row.form_type as RogerDocFormType, row.display_label);
  }

  type Acc = Map<string, Map<string, Map<string, BespokeTemplateExportRow[]>>>;

  const acc: Acc = new Map();

  for (const tmpl of params.templates) {
    // Rows are expected to already carry a resolved `form_type` (see
    // `resolveLegacyImportRowPins` for legacy bundles); anything still
    // unresolved falls into the "unknown" bucket rather than crashing.
    const formMapKey: string = tmpl.form_type || IMPORT_UNKNOWN_FORM_TYPE;
    const schema = tmpl.schema_version_name.trim();
    const section = tmpl.section_name.trim();

    let bySchema = acc.get(formMapKey);
    if (!bySchema) {
      bySchema = new Map();
      acc.set(formMapKey, bySchema);
    }
    let bySection = bySchema.get(schema);
    if (!bySection) {
      bySection = new Map();
      bySchema.set(schema, bySection);
    }
    const list = bySection.get(section) ?? [];
    list.push(tmpl);
    bySection.set(section, list);
  }

  function labelFor(formMapKey: string): {
    key: ImportFormTypeGroupingKey;
    display: string;
  } {
    if (formMapKey === IMPORT_UNKNOWN_FORM_TYPE) {
      return {
        key: IMPORT_UNKNOWN_FORM_TYPE,
        display:
          'Unknown form type (schema version name not in current catalog)',
      };
    }
    const ft = formMapKey as RogerDocFormType;
    return { key: ft, display: displayByFormType.get(ft) ?? formMapKey };
  }

  const result: ImportFormTypeGroup[] = [...acc.entries()]
    // The unknown bucket sorts last whatever it is named; everything else
    // follows the same precedence order as the dropdown and the validation
    // results, so a form sits in the same place in every list.
    .sort(([a], [b]) => compareUnknownLast(a, b))
    .map(([formMapKey, bySchema]) => {
      const { key: formTypeKey, display: formTypeDisplayLabel } =
        labelFor(formMapKey);

      const schemas: ImportSchemaGroup[] = [...bySchema.entries()]
        .sort(([snA], [snB]) =>
          snA.localeCompare(snB, undefined, { sensitivity: 'base' })
        )
        .map(([schema_version_name, bySection]) => {
          const sections: ImportSectionGroup[] = [...bySection.entries()].map(
            ([section_name, leaves]) => ({
              section_name,
              leaves: [...leaves],
            })
          );
          sortSections(sections);
          return { schema_version_name, sections };
        });

      return { formTypeKey, formTypeDisplayLabel, schemas };
    });

  return result;
}

export function bespokeRowsByLeafKey(
  rows: BespokeTemplateExportRow[]
): Map<string, BespokeTemplateExportRow> {
  const m = new Map<string, BespokeTemplateExportRow>();
  for (const row of rows) {
    m.set(bespokeTemplateLeafKey(row), row);
  }
  return m;
}

export function leafKeysUnderSection(sec: ImportSectionGroup): string[] {
  return sec.leaves.map(bespokeTemplateLeafKey);
}

export function leafKeysUnderSchema(sch: ImportSchemaGroup): string[] {
  return sch.sections.flatMap(leafKeysUnderSection);
}

export function leafKeysUnderFormType(ft: ImportFormTypeGroup): string[] {
  return ft.schemas.flatMap(leafKeysUnderSchema);
}

export function collectLeafKeysFromGroups(
  groups: ImportFormTypeGroup[]
): string[] {
  return groups.flatMap(leafKeysUnderFormType);
}

/**
 * Serializable row model for `SelectableNestedTable` (collapsible grouped rows +
 * divider chrome). Intended for bundles like bespoke template import previews.
 */
export type SelectableNestedTableRowModel = {
  /** Stable DOM / React key */
  id: string;
  /** Leaf keys governed by this row’s checkbox */
  branchLeafKeys: readonly string[];
  primaryLine: string;
  /** Inline taxonomy label (shown before primary text on parent rows). */
  categoryLine?: string | null;
  /** Bold primary text (form type tier only — other tiers stay regular weight). */
  primaryBold?: boolean;
  /** Muted identifier shown after the primary label (e.g. `RogerDocFormType` key). */
  primaryMeta?: string | null;
  children?: SelectableNestedTableRowModel[];
};

export function importNestedGroupsToSelectableNestedRows(
  groups: ImportFormTypeGroup[],
  formatSectionLabel: (sectionMachineName: string) => string
): SelectableNestedTableRowModel[] {
  return groups.map((ft, ftIx) => ({
    id: `import-ft-${ftIx}`,
    branchLeafKeys: leafKeysUnderFormType(ft),
    primaryLine: ft.formTypeDisplayLabel,
    categoryLine: 'Form type',
    primaryBold: true,
    children: ft.schemas.map((sch, schIx) => ({
      id: `import-ft-${ftIx}-sch-${schIx}`,
      branchLeafKeys: leafKeysUnderSchema(sch),
      primaryLine: sch.schema_version_name,
      categoryLine: 'Schema version',
      children: sch.sections.map((sec, secIx) => ({
        id: `import-ft-${ftIx}-sch-${schIx}-sec-${secIx}`,
        branchLeafKeys: leafKeysUnderSection(sec),
        primaryLine: formatSectionLabel(sec.section_name),
        categoryLine: 'Section',
        children: sec.leaves.map((row, leafIx) => ({
          id: `import-ft-${ftIx}-sch-${schIx}-sec-${secIx}-leaf-${leafIx}`,
          branchLeafKeys: [bespokeTemplateLeafKey(row)],
          primaryLine: row.name,
        })),
      })),
    })),
  }));
}

/** `true` | `false` | `'indeterminate'` from Radix Checkbox. */
export function triStateFromSelected(
  allKeys: string[],
  selected: ReadonlySet<string>
): boolean | 'indeterminate' {
  let n = 0;
  for (const k of allKeys) {
    if (selected.has(k)) n += 1;
  }
  if (n === 0) return false;
  if (n === allKeys.length) return true;
  return 'indeterminate';
}
